import React, {useEffect, useState} from 'react';
import {connect, DispatchProp} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import {getStoreConfig} from "../../../../services/Store/reducer";
import {RootState} from "../../../../../reducers";
import {ReactComponent as MenuSVG} from "../../../../../images/icons/menu.svg";
import {ReactComponent as BasketSVG} from "../../../../../images/icons/basket.svg";
import {ReactComponent as BellRingSVG} from "../../../../../images/icons/bell-ring.svg";
import StoreUtils from "../../../../services/Store/StoreUtils";
import {getUser} from "../../../../services/User/reducer";
import {api} from "../../../../services/Api/api";
import {getSession} from "../../../../services/Session/reducer";
import ChangeTableNumberModal from "../../../../components/Modal/ChangeTableNumberModal";
import {addFlash} from "../../../../services/Notifications/actions";
import handleException from "../../../../services/Api/handleException";
import Money from "../../../../components/Common/Money";
import HomepageMenusItem from "./HomepageMenusItem";

type Props = ReturnType<typeof mapStateToProps> & DispatchProp;

const HomePageMenusComponent = (props: Props) => {
    const [callWaiterLoading, setCallWaiterLoading] = useState(false);
    const [tableNumberModal, setTableNumberModal] = useState(false);
    const [tableOrders, setTableOrders] = useState<any[]>([]);
    const {config} = props;
    const {t} = useTranslation();
    const tableNumber = props.session.table_number;
    const orderTypes = config ? StoreUtils.getOrderTypes(config) : [];

    useEffect(() => {
        getTableOrders();
    }, []);

    if (!config) return null;

    const callWaiter = async () => {
        setCallWaiterLoading(true);

        if (tableNumber === undefined) {
            setTableNumberModal(true)
            setCallWaiterLoading(false);
            return;
        }

        try {
            await api.callWaiter(tableNumber);
            props.dispatch(addFlash('success', t("alert.success.call_waiter")));
        } catch (err) {
            const errors = handleException(err);
            props.dispatch(addFlash('error', errors));
        }
        setCallWaiterLoading(false);
    }

    const getTableOrders = async () => {
        if (!props.config?.table.payment) return;
        if (!StoreUtils.isTable(props.session)) return;
        if (!tableNumber) return;

        try {
            const response = await api.getTableOrders(tableNumber);
            setTableOrders(response);
        } catch (err) {
            const errors = handleException(err);
            props.dispatch(addFlash('error', errors));
        }
    }

    return (
        <>
            <ChangeTableNumberModal tableName={tableNumber} show={tableNumberModal}
                                    onSave={() => setTableNumberModal(false)}
                                    onHide={() => setTableNumberModal(false)}/>
            <div className={"homepage-menus"}>
                <div className={"container-fluid"}>
                    {(StoreUtils.isTable(props.session)) && (
                        <div className={"table"}>
                            <span>{`${t("common.word.table")}: `}</span>
                            <strong>{props.session.table_number}</strong>
                        </div>
                    )}
                    <div className="nav flex-column nav-pills w-100 nav-complex gap-2">
                        {(props.session.order_type === "DINE_IN" && props.config?.table.payment && tableOrders.length > 0) && (
                            <>
                                {tableOrders.map(tableOrder => {
                                    if (!tableOrder.url) return null;

                                    const tableOrderUrlBase = btoa(unescape(encodeURIComponent(tableOrder.url)));
                                    const isPaid = tableOrder.payment_status === "PAID";
                                    const toPay = (tableOrder.total_amount?.amount ?? 0) - (tableOrder.total_paid_amount?.amount ?? 0);
                                    const isPartiallyPaid = toPay > 0 && tableOrder.total_paid_amount?.amount;

                                    return (
                                        <HomepageMenusItem icon={<BasketSVG/>} link={`table_order/${tableOrderUrlBase}`}
                                                           title={isPaid ? t("modules.home_page.field.order_details.title") : t("modules.home_page.field.order.title")}
                                                           primaryBackground={!isPaid}
                                                           description={<>
                                                               <Money
                                                                   value={isPartiallyPaid ? toPay : tableOrder.total_amount}/> {isPartiallyPaid ? `(${t("modules.home_page.field.partially_paid.title")}) ` : ""}•
                                                               #{tableOrder.number}
                                                           </>}
                                        />
                                    )
                                })}
                            </>
                        )}
                        {(props.session.order_type === "DINE_IN" && props.config?.table.call_waiter) && (
                            <HomepageMenusItem icon={<BellRingSVG/>} onClick={callWaiter}
                                               title={t("modules.home_page.field.call_waiter.title")}
                                               description={t("modules.home_page.field.call_waiter.description")}
                                               loading={callWaiterLoading}
                            />
                        )}
                        <HomepageMenusItem icon={<MenuSVG/>} link="menu"
                                           title={t("common.word.menu")}
                                           description={orderTypes.map(x => t(`enums.order.type.${x}`)).join(", ")}
                        />
                    </div>
                </div>
            </div>
        </>
    )
}


const mapStateToProps = (state: RootState) => ({
    config: getStoreConfig(state.config),
    user: getUser(state.user),
    session: getSession(state.session)
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(HomePageMenusComponent));